import React, { useContext, useState, useEffect } from 'react';
import { Table, ColumnDefinition, RowDefinition, Button, Modal, useFlashMessage } from '@indigo-ag/component-module';
import { CreditConditionalItem, OpportunityType, PriorNegotiation } from '@indigo-ag/schema';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';

import i18next from 'core/i18n';
import { createStylesHook } from 'shared';
import { currency, date } from 'shared/utils';
import {
  getOpportunityTypeTranslation,
  getPaymentStatusTranslation
} from '../../DecisionSheet/utils/decisionSheet.util';
import { FinanceProcessContext } from '../../../pages/BrazilGrowersDetail';
import styled from 'styled-components';
import { getCreditApprovalData, importExceptionsAndConditionals } from '../../../queries/creditApproval.queries';

interface Props {
  priorNegotiation: PriorNegotiation;
}

interface Contract {
  id?: string;
  registrationDate?: Date;
  contractValue?: string;
  opportunityType: OpportunityType;
  campaignName: string;
  guarantyType?: string;
  paymentStatus?: string;
  status?: string;
}

const WordWrapCell = styled.div`
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: fit-content;
`;

const LinkWrapper = styled.div`
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const useStyles = createStylesHook({
  contract: {
    marginBottom: '12px',
    padding: '12px 0',
    boxShadow: '0px 2px 4px rgba(0,87,125,0.14), 0px 2px 2px rgba(0,87,125,0.1);',
    borderRadius: '6px',
    '& div[role="table"]': {
      '& div[role="rowgroup"] > div[aria-hidden="true"]': {
        boxShadow: 'none',
        marginTop: '0'
      },
      '& div[role="columnheader"]': {
        fontSize: '0.813rem',
        marginBottom: '0'
      },
      '& div[role="columnheader"], div[role="cell"]': {
        paddingLeft: '16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      '& div[role="cell"]': {
        minHeight: 'fit-content',
        marginTop: '0',
        height: 'auto',
        '&  > div': {
          padding: '0',
          '& > div': {
            color: 'black'
          }
        }
      }
    }
  }
});

type ColumnId = 'registrationDate' | 'contractValue' | 'opportunityType' | 'campaignName' | 'status';

const columnDefinition: ColumnDefinition<Contract, ColumnId>[] = [
  {
    id: 'registrationDate',
    header: i18next.t('registrationDate'),
    cell: ({ row: { registrationDate }, SimpleCell }) => (
      <SimpleCell label={date.formatDate(registrationDate, 'DD MMMM, YYYY')} />
    ),
    constraints: {
      min: { rem: 6 },
      max: { fr: 1 }
    }
  },
  {
    id: 'contractValue',
    header: `${i18next.t('contractValue')}`,
    cell: ({ row: { contractValue = '0' }, SimpleCell }) => (
      <SimpleCell label={currency.formatBrazilCurrency(contractValue || '0')} />
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'opportunityType',
    header: i18next.t('opportunityType'),
    cell: ({ row: { opportunityType } }) => (
      <WordWrapCell>{getOpportunityTypeTranslation(opportunityType as OpportunityType)}</WordWrapCell>
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'campaignName',
    header: i18next.t('campaign'),
    cell: ({ row: { campaignName } }) => <WordWrapCell>{campaignName}</WordWrapCell>,
    constraints: {
      min: { rem: 6 },
      max: { fr: 1 }
    }
  },
  {
    id: 'status',
    header: i18next.t('status'),
    cell: ({ row: { status }, SimpleCell }) => <SimpleCell label={getPaymentStatusTranslation(status)} />,
    constraints: {
      min: { rem: 6 },
      max: { fr: 1 }
    }
  }
];

export const Contract: React.FunctionComponent<Props> = ({ priorNegotiation }) => {
  const classes = useStyles();
  const { agFinance, creditApprovalDataQuery } = useContext(FinanceProcessContext);
  const { addFlashMessage } = useFlashMessage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, loading, error } = useQuery(getCreditApprovalData, {
    variables: { agFinancingId: selectedId },
    skip: !selectedId
  });

  const [importExceptionsAndConditionalsFn] = useMutation(importExceptionsAndConditionals);

  useEffect(() => {
    if (!selectedId) return;

    if (loading) {
      addFlashMessage({
        content: i18next.t('loading'),
        title: ''
      });
      return;
    }

    if (error) {
      addFlashMessage({
        content: i18next.t('error'),
        title: '',
        variant: 'error'
      });
      setSelectedId(null);
      return;
    }

    const creditApprovalData = data?.creditApprovalData;
    const creditExceptionsImported = creditApprovalData?.round?.creditExceptions || [];
    const creditConditionalsImported =
      creditApprovalData?.round?.creditConditionals.map((c: CreditConditionalItem) => c.name) || [];

    if (creditExceptionsImported.length > 0 || creditConditionalsImported.length > 0) {
      importExceptionsAndConditionalsFn({
        variables: {
          data: {
            id: agFinance?.id,
            importedExceptions: creditExceptionsImported,
            importedConditionals: creditConditionalsImported,
            parentId: selectedId
          }
        },
        refetchQueries: [
          {
            query: getCreditApprovalData,
            variables: { agFinancingId: agFinance?.id }
          }
        ],
        awaitRefetchQueries: true
      }).catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
    }

    addFlashMessage({
      content: i18next.t('exceptionsAndConditionalsImportedSuccessfully'),
      title: '',
      variant: 'success'
    });

    setSelectedId(null);
    setIsConfirmationModalOpen(false);
  }, [
    agFinance,
    data,
    loading,
    error,
    selectedId,
    addFlashMessage,
    importExceptionsAndConditionalsFn,
    creditApprovalDataQuery
  ]);

  const importConditionalsAndExceptions = (id: string) => {
    setPendingId(id);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmImport = () => {
    if (pendingId) {
      setSelectedId(pendingId);
      setPendingId(null);
    }
  };

  const handleCancelImport = () => {
    setIsConfirmationModalOpen(false);
    setPendingId(null);
  };

  const modalContent = () => (
    <div>
      <p>{i18next.t('confirmImportExceptionsAndConditionals')}</p>
    </div>
  );

  const modalActions = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0 2rem 1rem' }}>
      <Button onClick={handleCancelImport} variant="secondary" size="small">
        {i18next.t('cancel')}
      </Button>
      <Button onClick={handleConfirmImport} variant="primary" size="small">
        {i18next.t('confirm')}
      </Button>
    </div>
  );

  const rowDefinition: RowDefinition<Contract, ColumnId> = {
    rowId: ({ row: { id = '' } }) => id,
    actions: ({ row, ActionLinkText }) => (
      <LinkWrapper>
        {row.opportunityType === OpportunityType.BARTER_LIMIT && (
          <Link to="#" onClick={() => importConditionalsAndExceptions(row.id as string)} className="row-link">
            <ActionLinkText>{i18next.t('importConditionalsAndExceptions')}</ActionLinkText>
          </Link>
        )}
        <Link to={`/br-growers/${row.id}`} className="row-link">
          <ActionLinkText>{i18next.t('seeContract')}</ActionLinkText>
        </Link>
      </LinkWrapper>
    ),
    actionsLocation: 'footer'
  };

  const financingProcess = agFinance?.grower?.agFinancingProcesses?.find(agf => agf.id === priorNegotiation.id);
  const opportunityInfo = financingProcess?.orderInfo?.opportunities[0];
  const contract: Contract[] = [
    {
      id: priorNegotiation.id,
      registrationDate: priorNegotiation.date,
      contractValue: String(priorNegotiation.amount || opportunityInfo?.contractValue),
      guarantyType: priorNegotiation.collateralType,
      paymentStatus: priorNegotiation.paymentStatus,
      opportunityType: priorNegotiation.type,
      campaignName: priorNegotiation.campaign,
      status: priorNegotiation.status
    }
  ];

  return (
    <>
      <div className={classes.contract}>
        <Table
          label="Contract"
          columnDefinition={columnDefinition}
          rowDefinition={rowDefinition}
          currentColumns={['registrationDate', 'contractValue', 'opportunityType', 'campaignName', 'status']}
          data={contract}
        />
      </div>
      <Modal
        open={isConfirmationModalOpen}
        onRequestClose={handleCancelImport}
        title={i18next.t('importConditionalsAndExceptions')}
        renderContent={modalContent}
        renderActionArea={modalActions}
      />
    </>
  );
};
